import React, { useEffect, useRef } from "react";
import {
    Hammer,
    Layers,
    Paintbrush,
    Boxes,
    Home,
    Wrench,
    Building2,
    Ruler,
} from "lucide-react";

const services = [
    { icon: Hammer, title: "Maurerarbeiten", desc: "Klassisches Handwerk – präzise gesetzt, sauber verfugt." },
    { icon: Building2, title: "Umbauten", desc: "Neue Grundrisse und Nutzungen für bestehende Gebäude." },
    { icon: Home, title: "Renovationen", desc: "Instandsetzungen mit Respekt vor Substanz und Zeit." },
    { icon: Layers, title: "Betonarbeiten", desc: "Schalung, Bewehrung und Sichtbeton in bester Qualität." },
    { icon: Boxes, title: "Mauerwerk", desc: "Backstein, Kalksandstein, Naturstein – exakt vermauert." },
    { icon: Paintbrush, title: "Verputzarbeiten", desc: "Feine, glatte Flächen in Innen- und Aussenbereich." },
    { icon: Wrench, title: "Sanierungen", desc: "Umfassende Erneuerungen von Fassaden bis Kellermauern." },
    { icon: Ruler, title: "Kleinere Bauarbeiten", desc: "Auch der kleinste Auftrag verdient volle Präzision." },
];

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in-view"); }),
            { threshold: 0.12 }
        );
        el.querySelectorAll(".fade-up").forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function Services() {
    const ref = useReveal();
    return (
        <section
            id="leistungen"
            ref={ref}
            data-testid="services-section"
            className="relative bg-[#F5F5F5] py-28 lg:py-40"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 lg:mb-24">
                    <div className="lg:col-span-6">
                        <div className="eyebrow fade-up mb-6">Leistungen</div>
                        <h2
                            className="fade-up text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#14100D] leading-[1.02]"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            Acht Disziplinen.
                            <br />
                            <span className="font-medium">Ein Standard.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="fade-up mt-2 text-[15px] lg:text-[17px] leading-relaxed text-[#14100D]/70">
                            Jede Leistung wird von unserem Kernteam ausgeführt oder eng begleitet. So sichern wir gleichbleibende Qualität – vom kleinsten Detail bis zum Rohbau.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[rgba(20,16,13,0.1)]">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.title}
                                data-testid={`service-card-${i}`}
                                className="cler-card fade-up group p-8 lg:p-10 min-h-[240px] flex flex-col justify-between"
                                style={{ transitionDelay: `${i * 30}ms` }}
                            >
                                <Icon
                                    size={28}
                                    strokeWidth={1.4}
                                    className="text-[#14100D] group-hover:text-[#E20514] transition-colors duration-500"
                                />
                                <div>
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#14100D]/50 mb-2">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h3
                                        className="text-xl lg:text-2xl font-medium text-[#14100D] mb-3"
                                        style={{ fontFamily: "Outfit, sans-serif" }}
                                    >
                                        {s.title}
                                    </h3>
                                    <p className="text-[14px] leading-relaxed text-[#14100D]/70">
                                        {s.desc}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
